import {
  Box,
  Code,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  Tag,
  TagLabel,
  TagLeftIcon,
} from "@chakra-ui/react";
import { useFormikContext } from "formik";
import { InputControl } from "formik-chakra-ui";
import _ from "lodash";
import React, { useCallback, useMemo } from "react";
import { RiFlaskLine } from "react-icons/ri";
import { FormikSwitchInput } from "src/components/Inputs/FormikSwitchInput";
import { FormikSwitchableContent } from "src/components/Inputs/FormikSwitchableContent";
import { MultiSelectInput } from "src/components/Inputs/MultiSelectInput";
import { useEnrollmentPeriod } from "src/components/Providers/EnrollmentPeriodProvider";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import * as OrgConfig from "src/generated/hooks/org-config/requests";
import { useOrganization } from "src/hooks/useOrganization";
import * as OrgService from "src/services/orgService";
import * as Url from "src/services/url";
import * as RD from "src/types/remoteData";
import { z } from "zod";
import { BandListEditor } from "./BandListEditor";
import {
  BandFormikSchema,
  fromConfigBands,
  hasCatchAllBand,
  toConfigBands,
} from "./bandFields";

type PeriodOption = { id: string; name: string };

export const Form: React.FC = () => {
  const flags = useFlags(["avela-labs-capacity-availability"]);
  const organization = useOrganization();
  const { enrollmentPeriods } = useEnrollmentPeriod();
  const formik = useFormikContext<FormikType>();

  const periodOptions: PeriodOption[] = useMemo(
    () =>
      enrollmentPeriods.hasData()
        ? enrollmentPeriods.data.map((period) => ({
            id: period.id,
            name: period.name,
          }))
        : [],
    [enrollmentPeriods]
  );

  const selectedPeriods = useMemo(() => {
    const selected = new Set(
      formik.values.externalCapacity.enrollmentPeriodIds
    );
    return periodOptions.filter((period) => selected.has(period.id));
  }, [formik.values.externalCapacity.enrollmentPeriodIds, periodOptions]);

  if (!flags["avela-labs-capacity-availability"].enabled) return null;

  const org = organization.toNullable();
  const publicUrl =
    org === null
      ? undefined
      : `${window.location.origin}${Url.Parent.Availability.index(org)}`;

  return (
    <Flex direction="column" gap={2}>
      <Flex gap={2} alignItems="center">
        <Heading as="h3" variant="admin">
          Public seat availability
        </Heading>
        <Tag size="sm" variant="outline" colorScheme="purple">
          <TagLeftIcon as={RiFlaskLine} mr={1} />
          <TagLabel>Beta</TagLabel>
        </Tag>
      </Flex>

      <FormikSwitchableContent<"enabled" | "disabled">
        name="externalCapacity.status"
        label="Publish seat availability publicly"
        description="Shows a public, unauthenticated page listing each school and grade with a general availability range. Exact seat counts and waitlist lengths are never published."
        checked={["enabled", ""]}
        unchecked={["disabled", ""]}
        showContent={(value) => value === "enabled"}
      >
        <Flex direction="column" gap={4}>
          {publicUrl === undefined ? null : (
            <Box color="gray.600" fontSize="sm">
              Public page: <Code>{publicUrl}</Code>
            </Box>
          )}

          <InputControl
            label="Page title"
            name="externalCapacity.title"
            inputProps={{ placeholder: "e.g. Seat availability" }}
            isRequired
          />

          <InputControl
            label="Page description"
            name="externalCapacity.description"
            inputProps={{
              placeholder: "Optional text shown under the page title",
            }}
          />

          <FormControl>
            <FormLabel>Enrollment periods</FormLabel>
            <FormHelperText marginBottom={2}>
              Leave empty to publish every active enrollment period.
            </FormHelperText>
            <MultiSelectInput<PeriodOption>
              size="md"
              options={periodOptions}
              initialValues={selectedPeriods}
              onChange={(values) =>
                formik.setFieldValue(
                  "externalCapacity.enrollmentPeriodIds",
                  values.map((period) => period.id)
                )
              }
              labelAttr="name"
              placeholder="All active periods"
              renderDropdownItem={(item) => (
                <Box>
                  <span>{item.name}</span>
                </Box>
              )}
            />
          </FormControl>

          <FormikSwitchInput
            name="externalCapacity.showPrograms"
            label="Show programs"
            direction="row"
            checked={[true, ""]}
            unchecked={[false, ""]}
          />

          <FormikSwitchInput
            name="externalCapacity.showWaitlist"
            label="Show waitlist ranges"
            direction="row"
            checked={[true, ""]}
            unchecked={[false, ""]}
          />

          <BandListEditor
            name="externalCapacity.seatBands"
            label="Availability ranges"
            helperText="A school is labelled with the highest range whose seat count it meets."
            countLabel="Min seats open"
          />

          <BandListEditor
            name="externalCapacity.waitlistBands"
            label="Waitlist ranges"
            helperText="Applied to the number of families currently waitlisted for a school and grade."
            countLabel="Min waitlisted"
          />
        </Flex>
      </FormikSwitchableContent>
    </Flex>
  );
};

export function useService(): {
  initialValues: RD.RemoteData<Error, FormikType>;
  submit: (values: FormikType) => Promise<void>;
} {
  const { remoteData, update } =
    OrgService.useOrgConfig<OrgConfig.ExternalCapacity>("ExternalCapacity");

  const initialValues = useMemo(() => {
    return remoteData.map((config): FormikType => {
      const source = config ?? OrgConfig.ExternalCapacitySchema.default;
      const isEnabled = config !== null && !config.disabled && config.enabled;

      return {
        externalCapacity: {
          status: isEnabled ? "enabled" : "disabled",
          title: source.title,
          description: source.description,
          showPrograms: source.showPrograms,
          showWaitlist: source.showWaitlist,
          enrollmentPeriodIds: [...source.enrollmentPeriodIds],
          seatBands: fromConfigBands([...source.seatBands]),
          waitlistBands: fromConfigBands([...source.waitlistBands]),
        },
      };
    });
  }, [remoteData]);

  const submit = useCallback(
    async (values: FormikType) => {
      if (
        _.isEqual(
          values.externalCapacity,
          initialValues.toNullable()?.externalCapacity
        )
      ) {
        return;
      }

      const { externalCapacity } = values;
      await update({
        disabled: false,
        enabled: externalCapacity.status === "enabled",
        title: externalCapacity.title.trim(),
        description: externalCapacity.description.trim(),
        enrollmentPeriodIds: externalCapacity.enrollmentPeriodIds,
        showPrograms: externalCapacity.showPrograms,
        showWaitlist: externalCapacity.showWaitlist,
        seatBands: toConfigBands(externalCapacity.seatBands),
        waitlistBands: toConfigBands(externalCapacity.waitlistBands),
      });
    },
    [initialValues, update]
  );

  return { initialValues, submit };
}

const BandListSchema = z
  .array(BandFormikSchema)
  .min(1, "Add at least one range")
  .refine(hasCatchAllBand, {
    message: "Add a range with a threshold of 0 to cover every school",
  });

export const FormikSchema = z.object({
  externalCapacity: z.object({
    status: z.enum(["enabled", "disabled"]),
    title: z.string().min(1, "This field is required"),
    description: z.string(),
    showPrograms: z.boolean(),
    showWaitlist: z.boolean(),
    enrollmentPeriodIds: z.array(z.string()),
    seatBands: BandListSchema,
    waitlistBands: BandListSchema,
  }),
});

export type FormikType = z.infer<typeof FormikSchema>;
